import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { usePreset } from '@primeuix/themes';
import { Theme } from '@primeuix/styled';

import { environment } from 'src/environments/environment';
import { AppTheme } from '../../theme/app-theme';
import { QuoteDevilPreset } from '../../theme/quote-devil-preset';

export type BrokerId = '01' | '04';

/** Entra app-role value that grants a user access to each broker. */
export const BROKER_ROLE: Record<BrokerId, string> = {
  '01': 'Chill',
  '04': 'QuoteDevil',
};

/** The preset shape accepted by usePreset() / produced by definePreset(). */
type Preset = Parameters<typeof usePreset>[0];

export interface BrokerCampaigns {
  onboardingCampaignId: number;
  onboardingSupportCampaignId: number;
  onboardingNoEmailCampaignId: number;
  /** Entra group whose members populate the agent-assignment dropdown. */
  agentsGroupId: string;
  ticketEmail: string;
}

export interface Broker {
  id: BrokerId;
  name: string;
  /** Body class that scopes brand-specific styling hooks. */
  themeClass: string;
  /** PrimeNG preset applied at runtime via usePreset(). */
  preset: Preset;
  /** Document <title>. */
  title: string;
  /** Favicon href (relative to the app root). */
  favicon: string;
  /** Header logo asset. */
  logoHeader: string;
  /** Accessible logo alt text. */
  logoAlt: string;
  /** Environment-specific campaign IDs (merged from environment.brokers). */
  campaigns: BrokerCampaigns;
}

/** Static broker config; environment-specific campaign IDs are merged in `current`. */
type BrokerBase = Omit<Broker, 'campaigns'>;

const BROKERS: Record<BrokerId, BrokerBase> = {
  '01': {
    id: '01',
    name: 'Chill',
    themeClass: 'broker-chill',
    preset: AppTheme,
    title: 'Workflow App | Chill',
    favicon: 'favicon.ico',
    logoHeader: 'assets/chill-logo.svg',
    logoAlt: 'Chill Insurance',
  },
  '04': {
    id: '04',
    name: 'Quote Devil',
    themeClass: 'broker-qd',
    preset: QuoteDevilPreset,
    title: 'Workflow App | Quote Devil',
    favicon: 'faviconQD.ico',
    logoHeader: 'assets/devil-logo.png',
    logoAlt: 'Quote Devil Insurance',
  },
};

const ALL_THEME_CLASSES = Object.values(BROKERS).map((b) => b.themeClass);

const STORAGE_KEY = 'brokerId';

function isBrokerId(value: string | null | undefined): value is BrokerId {
  return value === '01' || value === '04';
}

/** Case-insensitive lookup of a query-string param (e.g. `brokerId`, `BrokerId`, `BROKERID`). */
export function getQueryParamCaseInsensitive(search: string, name: string): string | null {
  const target = name.toLowerCase();
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.toLowerCase() === target) {
      return value;
    }
  }
  return null;
}

/**
 * Maps a hostname to a broker for domain-based resolution (deployed envs).
 * Quote Devil domains contain "quotedevil"; everything else defaults to Chill.
 */
export function brokerIdFromHostname(hostname: string): BrokerId {
  return hostname.toLowerCase().includes('quotedevil') ? '04' : '01';
}

/**
 * Synchronously resolves the broker active at bootstrap, using the same
 * strategy as BrokerService.resolve(). Needed by app.config.ts to hand
 * providePrimeNG the correct preset before Angular is even running (PrimeNG
 * applies its bootstrap theme after the root component is constructed, so a
 * usePreset() call from the constructor would be overwritten).
 */
export function resolveInitialBrokerId(): BrokerId {
  if (environment.brokerResolution === 'domain') {
    return brokerIdFromHostname(window.location.hostname);
  }
  const raw =
    getQueryParamCaseInsensitive(window.location.search, 'brokerId') ??
    sessionStorage.getItem(STORAGE_KEY);
  return isBrokerId(raw) ? raw : isBrokerId(environment.defaultBrokerId) ? environment.defaultBrokerId : '01';
}

/** PrimeNG preset for the broker active at bootstrap (used in providePrimeNG). */
export function initialBrokerPreset(): Preset {
  return BROKERS[resolveInitialBrokerId()].preset;
}

/**
 * Single source of truth for the active broker. Resolves the broker from a raw
 * (query-param) value or the hostname, swaps the PrimeNG preset, toggles the
 * brand theme class on <body>, and updates the document title + favicon.
 */
@Injectable({ providedIn: 'root' })
export class BrokerService {
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);

  private readonly defaultBrokerId: BrokerId = isBrokerId(environment.defaultBrokerId)
    ? environment.defaultBrokerId
    : '01';

  private readonly _brokerId = signal<BrokerId>(this.defaultBrokerId);
  readonly brokerId = this._brokerId.asReadonly();
  readonly current = computed<Broker>(() => {
    const base = BROKERS[this._brokerId()];
    return { ...base, campaigns: environment.brokers[base.id] };
  });

  /**
   * Resolves the active broker using the environment's strategy:
   * - 'domain' (deployed): derived from the hostname; the query param is ignored.
   * - 'query'  (local):    read from the supplied brokerId query-param value,
   *   falling back to sessionStorage so the id survives the MSAL redirect
   *   round-trip (which drops query params).
   */
  resolve(queryBrokerId: string | null | undefined): void {
    if (environment.brokerResolution === 'domain') {
      this.setBroker(brokerIdFromHostname(this.document.location.hostname));
    } else {
      const raw = queryBrokerId ?? sessionStorage.getItem(STORAGE_KEY);
      this.setBroker(raw);
      sessionStorage.setItem(STORAGE_KEY, this._brokerId());
    }
  }

  /** Sets the active broker from a raw value, falling back to the default if unknown. */
  setBroker(raw: string | null | undefined): void {
    const id = isBrokerId(raw) ? raw : this.defaultBrokerId;
    if (id !== this._brokerId()) {
      this._brokerId.set(id);
    }
    this.applyTheme(BROKERS[id]);
  }

  private applyTheme(broker: BrokerBase): void {
    // Only swap the preset once PrimeNG's theme has been initialised by
    // providePrimeNG (always true in the app; not true in bare TestBeds,
    // where usePreset would corrupt the global theme state).
    if (Theme.theme) {
      usePreset(broker.preset);
    }

    const body = this.document.body;
    body.classList.remove(...ALL_THEME_CLASSES);
    body.classList.add(broker.themeClass);

    this.titleService.setTitle(broker.title);
    this.setFavicon(broker.favicon);
  }

  private setFavicon(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }
}
